import startCase from 'lodash/startCase'
import {
  PERMISSION_ENTITY_PURCHASE_ORDER,
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_OPERATIONAL,
  PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE,
  PERMISSION_ENTITY_INVOICE_OFFER
} from '@klear/constants'

import { KlearDate } from '@klear/date'
import type { CurrencyCode } from '@klear/constants/currency'
import { Money, sumByCurrency } from '@klear/money'
import { resolveDateDisplayInput } from '@klear/order-event/list-display'
import type { OrderEventDocumentDelta } from '@klear/order-event/order-event-log-change'
import type { OrderEventTimelineItem } from '@klear/order-event/order-event-timeline-tree'
import { timelineItemId, timelineItemParentId } from '@klear/order-event/order-event-timeline-tree'

export function orderEventTypeLabel(type: string): string {
  return startCase(type).replace(/ (Of|And|The) /g, (w) => w.toLowerCase())
}

export function isPurchaseOrderEvent(event: Pick<OrderEventTimelineItem, 'type'>): boolean {
  return event.type === PERMISSION_ENTITY_PURCHASE_ORDER
}

export function isInvoiceEvent(event: Pick<OrderEventTimelineItem, 'type'>): boolean {
  return event.type === PERMISSION_ENTITY_INVOICE
}

export function isOperationalEvent(
  event: OrderEventTimelineItem
): event is Extract<OrderEventTimelineItem, { type: typeof PERMISSION_ENTITY_OPERATIONAL }> {
  return event.type === PERMISSION_ENTITY_OPERATIONAL
}

export function isInvoiceOfferEvent(
  event: OrderEventTimelineItem
): event is Extract<OrderEventTimelineItem, { type: typeof PERMISSION_ENTITY_INVOICE_OFFER }> {
  return event.type === PERMISSION_ENTITY_INVOICE_OFFER
}

export function isProofOfAcceptanceType(type: string): boolean {
  return type === PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE
}

const TRADER_STATUS_EXCLUDED_EVENT_TYPES: readonly OrderEventTimelineItem['type'][] = [PERMISSION_ENTITY_INVOICE]

export function showsStatusToTrader(event: Pick<OrderEventTimelineItem, 'type'>): boolean {
  return !TRADER_STATUS_EXCLUDED_EVENT_TYPES.includes(event.type)
}

export function findTimelineEvent(
  events: readonly OrderEventTimelineItem[],
  id: number
): OrderEventTimelineItem | undefined {
  for (const event of events) {
    if (timelineItemId(event) === id) return event
    const found = findTimelineEvent(event.children, id)
    if (found) return found
  }
  return undefined
}

export function flattenTimelineEvents(events: readonly OrderEventTimelineItem[]): OrderEventTimelineItem[] {
  const out: OrderEventTimelineItem[] = []
  const walk = (list: readonly OrderEventTimelineItem[]) => {
    for (const event of list) {
      out.push(event)
      walk(event.children)
    }
  }
  walk(events)
  return out
}

export function timelineDocumentEventIds(events: readonly OrderEventTimelineItem[]): number[] {
  return flattenTimelineEvents(events).map(timelineItemId)
}

function invoicedMoniesForPurchaseOrder(
  flatEvents: readonly OrderEventTimelineItem[],
  purchaseOrderId: number
): Money[] {
  const today = KlearDate.today()
  return flatEvents
    .filter((event): event is Extract<OrderEventTimelineItem, { type: typeof PERMISSION_ENTITY_INVOICE }> => {
      if (!isInvoiceEvent(event)) return false
      if (timelineItemParentId(event) !== purchaseOrderId) return false
      const effectiveDate = resolveDateDisplayInput(event.model.effectiveDate)
      if (!effectiveDate) return false
      return effectiveDate.isSameOrBefore(today, 'day')
    })
    .map((invoice) => invoice.model.amount)
}

export function invoicedMoneyByCurrencyForPurchaseOrder(
  flatEvents: readonly OrderEventTimelineItem[],
  purchaseOrderId: number
): Map<CurrencyCode, Money> {
  return sumByCurrency(invoicedMoniesForPurchaseOrder(flatEvents, purchaseOrderId))
}

export function invoicedMoneyForPurchaseOrder(
  flatEvents: readonly OrderEventTimelineItem[],
  purchaseOrderId: number,
  purchaseOrderCurrency: CurrencyCode
): Money {
  const byCurrency = invoicedMoneyByCurrencyForPurchaseOrder(flatEvents, purchaseOrderId)
  return byCurrency.get(purchaseOrderCurrency) ?? Money.zero(purchaseOrderCurrency)
}

export function documentChangeLabel(update: OrderEventDocumentDelta): string | null {
  const parts: string[] = []
  if (update.documentsAdded > 0) parts.push(`attached ${documentCount(update.documentsAdded)}`)
  if (update.documentsRemoved > 0) parts.push(`removed ${documentCount(update.documentsRemoved)}`)
  return parts.length > 0 ? parts.join(', ') : null
}

function documentCount(count: number): string {
  return `${count} document${count === 1 ? '' : 's'}`
}

const TARGET_DELIVERY_STEP_LABEL = 'Target Delivery'
const DELIVERED_STEP_LABEL = 'Delivered'

type DeliveryMarker = { label: string; date: KlearDate }

export type TimelineStep<E> =
  | { kind: 'event'; event: E; epochDay: number }
  | { kind: 'delivery'; label: string; date: KlearDate; epochDay: number }

export function purchaseOrderDeliveryMarkers(purchaseOrder: {
  endDate: KlearDate
  deliveredDate?: KlearDate
}): DeliveryMarker[] {
  const markers: DeliveryMarker[] = [{ label: TARGET_DELIVERY_STEP_LABEL, date: purchaseOrder.endDate }]
  if (purchaseOrder.deliveredDate != null) {
    markers.push({ label: DELIVERED_STEP_LABEL, date: purchaseOrder.deliveredDate })
  }
  return markers
}

export function buildTimelineSteps<E>(
  events: readonly E[],
  eventEpochDay: (event: E) => number,
  deliveries: readonly DeliveryMarker[],
  sortOrder: 'newest' | 'oldest'
): TimelineStep<E>[] {
  const steps: TimelineStep<E>[] = events.map((event) => ({
    kind: 'event',
    event,
    epochDay: eventEpochDay(event)
  }))
  for (const delivery of deliveries) {
    steps.push({ kind: 'delivery', label: delivery.label, date: delivery.date, epochDay: delivery.date.epochDay })
  }
  const oldestFirst = steps.sort((a, b) => a.epochDay - b.epochDay || sameDayRank(a) - sameDayRank(b))
  return sortOrder === 'oldest' ? oldestFirst : oldestFirst.reverse()
}

function sameDayRank<E>(step: TimelineStep<E>): number {
  return step.kind === 'delivery' ? 0 : 1
}
